import { Button, ModalContent } from '@sandtech-hub-monorepo/ui-kit';
import {
  ConductCaseCategory,
  ConductCaseSeverity,
} from '@sandtech-hub-monorepo/ehub-client';
import { Link } from 'react-router-dom';
import { InfoIcon, MinusCircle, CircleX, LucideIcon } from 'lucide-react';
import { ReactComponent as FileXIcon } from '../../../../assets/Icons/file-x.svg';

const SCHOLASTIC_STATUSES_URL = 'https://www.elmwoodinstitute.org/catalog/';

interface Props {
  closeModal: () => void;
  severity: ConductCaseSeverity;
  category: ConductCaseCategory;
  reason: string;
  canAppeal: boolean;
  onAppeal: () => void;
}

const SEVERITY_TITLES: Record<ConductCaseSeverity, string> = {
  [ConductCaseSeverity.ScholasticWarning]:
    'You have been issued a Scholastic Warning',
  [ConductCaseSeverity.AcademicProbation]:
    'You have been placed on Academic Probation',
  [ConductCaseSeverity.AcademicSuspension]:
    'You have been issued an Academic Suspension',
  [ConductCaseSeverity.AcademicDismissal]:
    'Unfortunately, you have been withdrawn from your program',
};

const CATEGORY_SUBTITLES: Record<ConductCaseCategory, string> = {
  [ConductCaseCategory.GpaBelowMinimum]: 'GPA Below Minimum',
  [ConductCaseCategory.AcademicIntegrityViolation]:
    'Academic integrity violation',
};

type SeverityIcon = LucideIcon | typeof FileXIcon;

const SEVERITY_ICONS: Record<ConductCaseSeverity, SeverityIcon> = {
  [ConductCaseSeverity.ScholasticWarning]: InfoIcon,
  [ConductCaseSeverity.AcademicProbation]: FileXIcon,
  [ConductCaseSeverity.AcademicSuspension]: MinusCircle,
  [ConductCaseSeverity.AcademicDismissal]: CircleX,
};

const SEVERITY_ACCENT: Record<ConductCaseSeverity, string> = {
  [ConductCaseSeverity.ScholasticWarning]: 'text-warning',
  [ConductCaseSeverity.AcademicProbation]: 'text-destructive',
  [ConductCaseSeverity.AcademicSuspension]: 'text-destructive',
  [ConductCaseSeverity.AcademicDismissal]: 'text-destructive',
};

const SEVERITY_BG: Record<ConductCaseSeverity, string> = {
  [ConductCaseSeverity.ScholasticWarning]: 'bg-warning/10',
  [ConductCaseSeverity.AcademicProbation]: 'bg-destructive/10',
  [ConductCaseSeverity.AcademicSuspension]: 'bg-destructive/10',
  [ConductCaseSeverity.AcademicDismissal]: 'bg-destructive/10',
};

export const StandingDetailModal = ({
  closeModal,
  severity,
  category,
  reason,
  canAppeal,
  onAppeal,
}: Props) => {
  const Icon = SEVERITY_ICONS[severity];
  const reasonParagraphs = reason.split(/\n{2,}/).filter((p) => p.trim());
  const isDismissal = severity === ConductCaseSeverity.AcademicDismissal;
  return (
    <ModalContent>
      <div className="flex flex-col items-center text-center gap-8 max-w-[524px] mx-auto">
        <div className="flex flex-col items-center gap-4">
          <div
            className={`p-[25px] rounded-full flex items-center justify-center ${SEVERITY_BG[severity]}`}
          >
            <Icon
              height="48"
              width="48"
              className={SEVERITY_ACCENT[severity]}
            />
          </div>
          <h2
            className={`font-semibold text-2xl leading-8 ${SEVERITY_ACCENT[severity]}`}
          >
            {SEVERITY_TITLES[severity]}
          </h2>
        </div>

        <div className="flex flex-col gap-5 items-center w-full">
          <p className="text-foreground text-base font-semibold">
            {CATEGORY_SUBTITLES[category]}
          </p>
          {reasonParagraphs.map((paragraph, index) => (
            <p key={index} className="text-muted-foreground text-sm">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="flex gap-3 w-full justify-center flex-wrap">
          <Link
            to={SCHOLASTIC_STATUSES_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" onClick={closeModal}>
              About scholastic statuses
            </Button>
          </Link>
          {!isDismissal && (
            <Button onClick={onAppeal} disabled={!canAppeal}>
              Appeal
            </Button>
          )}
        </div>
      </div>
    </ModalContent>
  );
};
